"use client";

import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { api } from "~/trpc/react";
import { EXTERNAL_URLS } from "~/lib/external-urls";
import { 
  StatCard, 
  ActionButton, 
  SectionCard, 
} from "~/components/ui";
import {
  SearchIcon,
  UploadIcon,
  FileIcon,
  LicenseVerificationIcon,
  EditIcon,
  FilePlusIcon,
  GlobeIcon,
  UsersIcon,
} from "~/components/icons";

export function ReadOnlyDashboardContent() {
  const router = useRouter();
  const { data: session } = useSession();

  const userId = session?.user?.id ? parseInt(session.user.id) : null;
  
  const { data: userActivity, isLoading: isLoadingActivity } = api.accounts.getUserActivity.useQuery(
    { id: userId ?? 0 },
    { enabled: !!userId }
  );

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return "—";
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(new Date(date));
  };

  const lastSearchDate = userActivity?.lastSearchDate ?? null;
  const lastUpdatedDate = userActivity?.lastUpdatedDate ?? null;
  const totalRecords = userActivity?.totalRecords ?? 0;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          label="Last Search"
          value={isLoadingActivity ? "..." : formatDate(lastSearchDate)}
          icon={<SearchIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Last Updated"
          value={isLoadingActivity ? "..." : formatDate(lastUpdatedDate)}
          icon={<EditIcon className="h-5 w-5 text-[#003261]" />}
        />
        <StatCard
          label="Total Records"
          value={isLoadingActivity ? "..." : formatNumber(totalRecords)}
          icon={<GlobeIcon className="h-5 w-5 text-[#003261]" />}
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Search Section */}
        <SectionCard 
          title="Search" 
          className="w-full"
        >
          <div className="flex flex-col">
            <ActionButton
              title="Search Actions"
              description="Look up a person or company in the DADB"
              icon={<UsersIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/search/actions')}
            />
            <ActionButton
              title="Bulk Search with File"
              description="Search DADB using files uploaded"
              icon={<UploadIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/search/bulk')}
            />
            <ActionButton
              title="Search History"
              description="Search disciplinary action database"
              icon={<FileIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/search/history')}
            />
            <ActionButton
              title="License Verification"
              description="Set up jurisdictions"
              icon={<LicenseVerificationIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => {
                window.open(EXTERNAL_URLS.ARELLO.PROGRAMS.LICENSE_VERIFICATION as string, '_blank', 'noopener,noreferrer');
              }}
            />
          </div>
        </SectionCard>

        {/* Account Settings Section */}
        <SectionCard 
          title="Account Settings" 
          className="w-full"
        >
          <div className="flex flex-col">
            <ActionButton
              title="Edit Account"
              description="Make changes to your account information."
              icon={<EditIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/settings')}
            />
            <ActionButton
              title="Change password"
              description="Update your password securely."
              icon={<FilePlusIcon className="h-4 w-4 text-[#003261]" />}
              onClick={() => router.push('/settings/change-password')}
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

